import type { LucideIcon } from "lucide-react";

import { StevieCitationList } from "./StevieCitationList";
import { StevieStatusIcon } from "./StevieStatusIcon";
import { stevieStatusRowClassName } from "./stevieStatusStyles";

import { Column, Row, Text } from "@/components";

import { cn } from "@/lib/utils";
import {
  CallStructuredSummaryField,
  CallStructuredSummaryStatus,
} from "@/types";

// One field, in either shape the payload takes: a single value, or a bullet
// list. Clicking the row opens its evidence and seeks the player to the first
// citation, so a recruiter can hear the moment a claim came from.
//
// Renders as two subgrid cells (label, value) rather than a wrapper element —
// a flex or block wrapper would break the parent's column tracks and drop the
// value below its label.
export const StevieFieldRow = ({
  field,
  hideStatusIcon = false,
  Icon,
  isExpanded,
  label,
  onSeek,
  onToggle,
}: {
  field: CallStructuredSummaryField;
  // Catalogs that do not grade an answer against a role show no status marker.
  hideStatusIcon?: boolean;
  // Highlight rows carry their own icon; grouped rows sit under the group's.
  Icon?: LucideIcon;
  isExpanded: boolean;
  label: string;
  onSeek: (timestamp: string) => void;
  onToggle: () => void;
}) => {
  /* Helpers */
  const isUnknown = field.status === CallStructuredSummaryStatus.UNKNOWN;
  const hasCitations = field.citations.length > 0;

  /* Handlers */
  const handleRowClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!hasCitations) return;
    // A timestamp button inside the open panel shouldn't collapse the row.
    if ((event.target as Element).closest("button")) return;

    onToggle();
    const firstCitation = field.citations.at(0);
    if (!isExpanded && firstCitation?.timestamp) {
      onSeek(firstCitation.timestamp);
    }
  };

  /* Render */
  const renderLabel = () => {
    if (Icon) {
      return (
        <Row className="h-5 items-center gap-3 whitespace-nowrap">
          <Icon className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.5} />
          <Text className="translate-y-px font-plex-mono text-[11px] font-medium uppercase leading-5 tracking-wider text-slate-500">
            {label}
          </Text>
        </Row>
      );
    }

    return (
      <Text className="pl-7 text-xs font-normal leading-5 text-slate-500">
        {label}
      </Text>
    );
  };

  const renderValue = () => {
    if (isUnknown) {
      return (
        <Text className="text-sm font-normal leading-5 text-slate-400">
          N/A
        </Text>
      );
    }
    if (field.bullets.length > 0) {
      return (
        <Column className="min-w-0 flex-1 gap-1">
          {field.bullets.map((bullet) => (
            <Row className="items-start gap-2" key={bullet}>
              {field.bullets.length > 1 ? (
                <Text className="text-sm font-normal leading-5 text-slate-400">
                  &bull;
                </Text>
              ) : null}
              <Text className="min-w-0 flex-1 text-sm font-normal leading-5 text-[#121212] dark:text-[#FAFAFA]">
                {bullet}
              </Text>
            </Row>
          ))}
        </Column>
      );
    }

    return (
      <Column className="min-w-0 flex-1 gap-0.5">
        <Text className="text-sm font-normal leading-5 text-[#121212] dark:text-[#FAFAFA]">
          {field.value ?? "N/A"}
        </Text>
        {field.detail ? (
          <Text className="text-xs font-normal text-slate-500">
            {field.detail}
          </Text>
        ) : null}
      </Column>
    );
  };

  const render = () => {
    return (
      <>
        <div
          className={cn(
            "col-span-2 -mx-2 grid grid-cols-subgrid items-start gap-x-3 rounded-[4px] px-2 py-1",
            hasCitations &&
              "cursor-pointer transition-colors hover:bg-slate-50",
            stevieStatusRowClassName(field.status),
          )}
          onClick={handleRowClick}
        >
          {renderLabel()}
          <Row className="min-h-5 min-w-0 items-start justify-between gap-2 pl-3">
            {renderValue()}
            {hideStatusIcon ? null : (
              <StevieStatusIcon className="mt-px" status={field.status} />
            )}
          </Row>
        </div>
        {isExpanded && hasCitations ? (
          <div className="col-span-2">
            <StevieCitationList
              citations={field.citations}
              onClose={onToggle}
              onSeek={onSeek}
            />
          </div>
        ) : null}
      </>
    );
  };

  return render();
};
